import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import {
  StripeCardNumberElement,
  Stripe,
  StripeCardElement,
} from '@stripe/stripe-js'
import {useState} from 'react'
import {DonationPurpose, Metadata} from 'components/DonationBox/shared'
import * as Sentry from '@sentry/browser'
import HumanFeedbackError from 'lib/HumanFeedbackError'
import env from 'lib/env'
import {Locale} from 'i18n'
import ky from 'ky'

const DONATION_API = env.GATSBY_DONATION_API

function isDonateOnceSuccessResponse(
  res: unknown,
): res is {client_secret: string} {
  return typeof res === 'object' && !!res && 'client_secret' in res
}

function isRecurrentDonationResponse(res: unknown): res is {success: boolean} {
  return typeof res === 'object' && res !== null && 'success' in res
}

interface OrderData {
  amount: number
  first_name: string
  last_name: string
  email: string
  charity_organization: DonationPurpose
  metadata: Metadata | null
  language: Locale
  recaptcha: string
  support_ansar: boolean
}

async function handleOneTimePayment(
  stripe: Stripe,
  cardElement: StripeCardNumberElement | StripeCardElement,
  orderData: OrderData,
  payerName: string,
) {
  const response: unknown = await ky(`${DONATION_API}donate_once/stripe`, {
    method: 'POST',
    json: orderData,
  }).json()

  if (!isDonateOnceSuccessResponse(response)) {
    throw new Error(
      response
        ? JSON.stringify(response)
        : "An unknown error from our API has occurred on endpoint 'donate_once'",
    )
  }
  const clientSecret = response.client_secret

  const result = await stripe.confirmCardPayment(clientSecret, {
    payment_method: {
      card: cardElement,
      billing_details: {
        name: payerName,
      },
    },
  })

  // The payment has been processed!
  if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
    await ky(
      `${DONATION_API}donate_once/stripe/verify/${result.paymentIntent.id}`,
      {
        method: 'POST',
      },
    ).json()
    return {success: true, message: 'Payment successful'}
  }
  if (result.error && result.error.message) {
    // Show error to your customer (e.g., insufficient funds)
    throw new HumanFeedbackError(result.error.message, result.error.message)
  }
  throw new Error('An unknown error has occurred')
}

async function handleRecurrentPayment(
  stripe: Stripe,
  cardElement: StripeCardNumberElement | StripeCardElement,
  orderData: OrderData,
  payerName: string,
) {
  const {paymentMethod, error} = await stripe.createPaymentMethod({
    type: 'card',
    card: cardElement,
    billing_details: {
      name: payerName,
    },
  })
  if (error) {
    throw new Error(error.message)
  }
  if (!paymentMethod) {
    throw new Error('We could not create a payment method')
  }
  const response: unknown = await ky(
    `${DONATION_API}recurrent_donation/stripe`,
    {
      method: 'POST',
      json: {
        ...orderData,
        stripe_payment_id: paymentMethod.id,
      },
    },
  ).json()

  if (!isRecurrentDonationResponse(response) || !response.success) {
    throw new Error(
      response
        ? JSON.stringify(response)
        : "An unknown error from our API has occurred on endpoint 'recurrent_donation/stripe'",
    )
  }
  return {success: true, message: 'Recurrent payment created successfully'}
}

interface IInitiatePaymentArgs {
  amount: number
  ansar: boolean
  firstName: string
  lastName: string
  email: string
  donationPurpose: DonationPurpose
  payerName: string
  metadata: Metadata | null
  isRecurrent: boolean
  language: Locale
  recaptcha: string
}

const useStripePayment = () => {
  const [loading, setLoading] = useState(false)
  const stripe = useStripe()
  const elements = useElements()
  const initiatePayment = async ({
    amount,
    ansar,
    firstName,
    lastName,
    email,
    donationPurpose,
    payerName,
    metadata,
    isRecurrent,
    language,
    recaptcha,
  }: IInitiatePaymentArgs): Promise<{success: boolean; message: string}> => {
    try {
      if (!stripe || !elements) {
        throw new Error(`Stripe didn't load before the payment was initiated?!`)
      }
      if (loading) {
        throw new Error(`We already have a payment processing`)
      }
      setLoading(true)

      const orderData: OrderData = {
        amount: amount,
        first_name: firstName,
        last_name: lastName,
        email: email,
        charity_organization: donationPurpose,
        metadata,
        language,
        recaptcha,
        support_ansar: ansar
      }
      const cardElement = elements.getElement(CardNumberElement)
      if (!cardElement) {
        throw new Error('We could not get the cardElement')
      }
      if (isRecurrent) {
        return await handleRecurrentPayment(
          stripe,
          cardElement,
          orderData,
          payerName,
        )
      } else {
        return await handleOneTimePayment(
          stripe,
          cardElement,
          orderData,
          payerName,
        )
      }
    } catch (e) {
      // TODO: attach user info to sentry
      // https://docs.sentry.io/enriching-error-data/context/?platform=browser
      console.error(e)
      Sentry.captureException(e)

      if (e instanceof HumanFeedbackError) {
        return {
          success: false,
          message: e.humanMessage,
        }
      } else if (e instanceof Error) {
        return {
          success: false,
          message:
            'An error has occurred, we will investigate. Please try again and contact us if it persist',
        }
      } else {
        throw e
      }
    } finally {
      setLoading(false)
    }
  }
  return {loading, initiatePayment}
}

export default useStripePayment
